import { Stack, Typography, useTheme } from "@mui/material";
import { upperFirst } from "lodash-es";
import type { FC } from "react";
import { AdvisorActionButtons } from "web_myplann/Advisor/Components/AdvisorActionButtons";
import { AdvisorContactLines } from "web_myplann/Advisor/Components/AdvisorContactLines";
import { AdvisorIntroduction } from "web_myplann/Advisor/Components/AdvisorIntroduction";
import { AdvisorMainBadge } from "web_myplann/Advisor/Components/AdvisorMainBadge";
import type { AdvisorSpecializationKey } from "web_myplann/Advisor/Types/AdvisorSpecializationKey";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { InitialsAvatar } from "web_myplann/Shared/Components/InitialsAvatar";
import { useOpenAgentChat } from "web_myplann/Shared/Hooks/useOpenAgentChat";

const R = Resources.Advisor;

type Props = {
	id: string;
	name: string;
	job?: string;
	specializations: AdvisorSpecializationKey[];
	hspName?: string;
	phone?: string;
	email?: string;
	address?: string;
	linkedin?: string;
	website?: string;
	description?: string;
	photo?: string;
	conversationId?: number;
	chatTitle: string;
	unreadCount: number;
	showMainBadge?: boolean;
	avatarMuted?: boolean;
	worksWithName?: string;
	introBeforeActions?: boolean;
};

export const AdvisorCard: FC<Props> = ({
	id,
	name,
	job,
	specializations,
	hspName,
	phone,
	email,
	address,
	linkedin,
	website,
	description,
	photo,
	conversationId,
	chatTitle,
	unreadCount,
	showMainBadge,
	avatarMuted,
	worksWithName,
	introBeforeActions,
}) => {
	const theme = useTheme();
	const { t } = useResource();
	const openChat = useOpenAgentChat();
	const specializationText = upperFirst(
		specializations.map((key) => t(R.specializations[key])).join(", "),
	);

	return (
		<Stack bgcolor={theme.palette.background.paper} pb={theme.spacingTokens.lg}>
			<Stack
				px={theme.spacingTokens.lg}
				pt={theme.spacingTokens.lg}
				alignItems="center"
			>
				<InitialsAvatar
					name={name}
					photo={photo}
					muted={avatarMuted}
					mb={theme.spacingTokens.md}
				/>

				<Stack
					direction="row"
					alignItems="center"
					justifyContent="center"
					flexWrap="wrap"
					gap={theme.spacingTokens.sm}
				>
					<Typography variant="h3" textAlign="center">
						{name}
					</Typography>
					{showMainBadge && <AdvisorMainBadge />}
				</Stack>

				{job && (
					<Typography
						variant="body2"
						color={theme.palette.grey[500]}
						mt={theme.spacingTokens.xs}
						textAlign="center"
						sx={{ overflowWrap: "anywhere" }}
					>
						{job}
					</Typography>
				)}

				{worksWithName && (
					<Typography
						variant="caption"
						color={theme.palette.grey[500]}
						mt={theme.spacingTokens.sm}
						textAlign="center"
						sx={{ overflowWrap: "anywhere" }}
					>
						{t(R.worksWith, { name: worksWithName })}
					</Typography>
				)}

				{specializationText && (
					<Typography variant="hint" textAlign="center">
						{specializationText}
					</Typography>
				)}

				{hspName && (
					<Typography
						variant="bodyMedium"
						color={theme.palette.grey[500]}
						mt={theme.spacingTokens.sm}
						textAlign="center"
						sx={{ overflowWrap: "anywhere" }}
					>
						{hspName}
					</Typography>
				)}

				<AdvisorContactLines
					phone={phone}
					email={email}
					address={address}
					linkedin={linkedin}
					website={website}
					mt={theme.spacingTokens.lg}
					mb={theme.spacingTokens.xl}
				/>
			</Stack>

			{description && introBeforeActions && (
				<AdvisorIntroduction
					text={description}
					mt={theme.spacingTokens.none}
					mb={theme.spacingTokens.lg}
				/>
			)}

			<AdvisorActionButtons
				px={theme.spacingTokens.lg}
				phone={phone}
				unreadCount={unreadCount}
				onOpenChat={() =>
					openChat({ uid: id, title: chatTitle, conversationId })
				}
			/>

			{description && !introBeforeActions && (
				<AdvisorIntroduction text={description} />
			)}
		</Stack>
	);
};
